import { useEffect, useState } from 'react';
import { z } from 'zod';

/**
 * Wire shape returned by the public licensing-server endpoint
 * `GET /broadcasts/:tenantId`. Mirrors the server-side `toPublic`
 * projection — narrower than the full BroadcastRow (no tenantId,
 * targetsJson, createdAt, or startsAt: the server has already filtered
 * to "active right now" rows before the response).
 */
export const PublicBroadcast = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
  expiresAt: z.number(),
  dismissible: z.boolean(),
});
export type PublicBroadcast = z.infer<typeof PublicBroadcast>;

const ResponseShape = z.object({ broadcasts: z.array(PublicBroadcast) });

/** Polling cadence in ms. 60 s lines up with the design note in app.ts. */
const POLL_INTERVAL_MS = 60_000;

export interface UseBroadcastPollerOptions {
  /**
   * Endpoint to poll. Pass `null` to disable polling (no tenant in URL,
   * no template configured, or feature flag not entitled).
   */
  url: string | null;
  /** Override for tests — defaults to globalThis.fetch. */
  fetchImpl?: typeof fetch;
  /** Override for tests — defaults to 60 s. */
  intervalMs?: number;
}

/**
 * React hook that polls for active broadcasts. Empties the list when
 * disabled, and survives transient fetch errors by keeping the previous
 * snapshot (no flicker on a single bad poll). Skipping locally-dismissed
 * ids is the caller's job — the hook owns the wire state.
 */
export function useBroadcastPoller({
  url,
  fetchImpl,
  intervalMs = POLL_INTERVAL_MS,
}: UseBroadcastPollerOptions): readonly PublicBroadcast[] {
  const [broadcasts, setBroadcasts] = useState<readonly PublicBroadcast[]>([]);

  useEffect(() => {
    if (!url) {
      setBroadcasts([]);
      return undefined;
    }
    let cancelled = false;
    const doFetch = fetchImpl ?? fetch;

    async function pollOnce(): Promise<void> {
      try {
        const res = await doFetch(url!, { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const json: unknown = await res.json();
        const parsed = ResponseShape.safeParse(json);
        if (!parsed.success || cancelled) return;
        setBroadcasts(parsed.data.broadcasts);
      } catch {
        // Network blips keep the previous list — don't flash empty.
      }
    }

    void pollOnce();
    const handle = setInterval(() => void pollOnce(), intervalMs);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [url, fetchImpl, intervalMs]);

  return broadcasts;
}

/** Local-only "I read this" set, persisted to localStorage by tenant. */
const DISMISSED_KEY = 'mobilauncher.broadcasts.dismissed';

export function loadDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

export function persistDismissedIds(ids: ReadonlySet<string>): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch {
    // Out of storage / disabled — best-effort; the panel still works.
  }
}
